"use client";
import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  ReactNode,
} from "react";
import { ApiClient } from "@kys/api-client";
import type { Locale, User, Taxon } from "@/lib/types";
const errors: Record<string, [string, string]> = {
  unauthorized: ["Please sign in to continue.", "请登录后继续。"],
  verify_first: [
    "Verify your email before contributing.",
    "投稿前请先验证您的邮箱。",
  ],
  forbidden: [
    "You do not have access to this action.",
    "您没有执行此操作的权限。",
  ],
  rate_limit: [
    "Too many requests. Please try again later.",
    "请求过于频繁，请稍后再试。",
  ],
  credentials: [
    "The details could not be verified. Please try again.",
    "无法验证您的信息，请重试。",
  ],
  account_exists: [
    "An account already uses this email. Try signing in or resetting your password.",
    "该邮箱已有账户，请登录或重置密码。",
  ],
  invalid: [
    "Please check the required fields and try again.",
    "请检查必填项后重试。",
  ],
  unavailable: [
    "This service is temporarily unavailable. Your input has been preserved.",
    "服务暂时不可用，您输入的内容已保留。",
  ],
  provider_unavailable: [
    "This sign-in provider is not connected yet.",
    "此登录服务尚未连接。",
  ],
  mail_failed: [
    "We couldn’t send your email. Please try again shortly or use another sign-in method.",
    "邮件发送失败，请稍后重试或使用其他登录方式。",
  ],
  invalid_code: [
    "This code is incorrect, expired, or already used. Check the latest email or request a new code.",
    "验证码错误、已过期或已使用。请查看最新邮件或重新获取验证码。",
  ],
  file_size: ["Choose a video smaller than 50 MB.", "请选择小于50 MB的视频。"],
  file_type: ["Choose a valid MP4 or MOV video.", "请选择有效的MP4或MOV视频。"],
  duplicate: [
    "This entry or video has already been submitted.",
    "此词条或视频已提交。",
  ],
  expired: [
    "This link has expired or was already used. Request a new one.",
    "此链接已过期或已使用，请申请新链接。",
  ],
  feedback_required: [
    "Add feedback before making this decision.",
    "请先填写反馈意见。",
  ],
  processing: [
    "The video must finish processing before approval.",
    "视频处理完成后才能批准。",
  ],
  own_like: [
    "You cannot like your own submission.",
    "您不能为自己的投稿点赞。",
  ],
  self_review: [
    "Another moderator must review your contribution.",
    "您的投稿需要其他审核员审核。",
  ],
  self_change: [
    "You cannot change your own role or account status.",
    "您不能更改自己的角色或账户状态。",
  ],
  not_found: ["This item is no longer available.", "此内容已不可用。"],
  origin: [
    "Please open the site using its configured address and try again.",
    "请通过配置的网站地址访问后重试。",
  ],
  challenge: ["Please complete the verification challenge.", "请完成验证。"],
  oauth_failed: [
    "Sign-in could not be completed. If this email already has an account, use your original sign-in method.",
    "登录未完成。如果此邮箱已有账户，请使用原来的登录方式。",
  ],
  oauth_cancelled: [
    "Sign-in was cancelled. You can try again whenever you’re ready.",
    "登录已取消，您可以随时重试。",
  ],
  oauth_conflict: [
    "This provider is already linked to another account. Use that account's sign-in method.",
    "此登录方式已关联其他账户，请使用该账户的登录方式。",
  ],
  oauth_email: [
    "Verify your primary email with the provider before signing in.",
    "请先在登录服务中验证您的主要邮箱。",
  ],
};
export function errorText(e: unknown): [string, string] {
  return errors[e instanceof Error ? e.message : "unavailable"] || errors.unavailable;
}
const client = new ApiClient('/api/v1');
export const apiUrl = (path: string) => '/api/' + path;
export async function api<T = any>(path: string, body?: unknown): Promise<T> {
  return client.request<T>(path, body);
}type Context = {
  locale: Locale;
  t: (en: string, zh: string) => string;
  setLocale: (v: Locale) => void;
  user: User | null;
  ready: boolean;
  languages: Taxon[];
  categories: Taxon[];
  providers: Record<string, boolean>;
  development: boolean;
  emailDelivery?: "resend" | "smtp" | "local" | "unavailable";
  turnstile: string | null;
  refresh: () => Promise<void>;
  message: (value: string) => void;
  error: (e: unknown) => void;
};
const Context = createContext<Context>(null!);
export function Provider({ children, initialData }: { children: ReactNode, initialData?: any }) {
  const [locale, setLanguage] = useState<Locale>("en"),
    [data, setData] = useState<any>(initialData || {
      user: null,
      languages: [],
      categories: [],
      providers: {},
    }),
    [ready, setReady] = useState(!!initialData),
    [notice, setNotice] = useState("");
  const t = useCallback(
    (en: string, zh: string) => (locale === "zh" ? zh : en),
    [locale],
  );
  const error = useCallback(
    (e: unknown) => {
      const pair = errorText(e);
      setNotice(t(...pair));
    },
    [t],
  );
  const refresh = useCallback(async () => {
    try {
      setData(await api("bootstrap"));
    } catch (e) {
      error(e);
    } finally {
      setReady(true);
    }
  }, [error]);
  useEffect(() => {
    const saved = localStorage.getItem("kys-locale");
    if (saved === "zh") setLanguage("zh");
  }, []);
  useEffect(() => {
    document.documentElement.lang = locale === "zh" ? "zh-Hans" : "en";
  }, [locale]);
  useEffect(() => {
    if (!initialData) void refresh();
  }, [refresh, initialData]);
  function setLocale(value: Locale) {
    setLanguage(value);
    localStorage.setItem("kys-locale", value);
  }
  return (
    <Context.Provider
      value={{
        locale,
        t,
        setLocale,
        ...data,
        ready,
        refresh,
        message: setNotice,
        error,
      }}
    >
      {children}
      {notice && (
        <div className="toast" role="alert">
          <span>{notice}</span>
          <button
            aria-label={t("Dismiss", "关闭")}
            onClick={() => setNotice("")}
          >
            ×
          </button>
        </div>
      )}
    </Context.Provider>
  );
}
export const useApp = () => useContext(Context);
